import React, { ReactNode } from 'react';
import { Zap, Calendar, Target, AlertTriangle } from 'lucide-react';
import BaseTrajectoryCard, { 
  BaseTrajectoryProps, 
  ThemeVocabulary, 
  TrajectoryCardRenderer 
} from './BaseTrajectoryCard';

// Ninjago theme trajectory card implementation
class NinjagoTrajectoryCardImplementation extends BaseTrajectoryCard {
  
  getThemeVocabulary(): ThemeVocabulary {
    return {
      unit: 'energy',
      pace: 'energy/day',
      goal: 'Golden Nintendo Switch',
      completion: 'mastered',
      current: 'Fire Power',
      remaining: 'Energy Required'
    };
  }
  
  getContainerClassName(): string {
    return "bg-gradient-to-br from-gray-900 via-red-900 to-orange-900 rounded-2xl p-6 border-2 border-orange-500 shadow-lg relative overflow-hidden";
  }
  
  renderHeader(): ReactNode {
    const status = this.determineStatus();
    const statusConfig = {
      excellent: { bg: 'bg-yellow-500/20', text: 'text-yellow-300', border: 'border-yellow-400', label: '🌟 Legendary Master!' },
      good: { bg: 'bg-blue-500/20', text: 'text-blue-300', border: 'border-blue-400', label: '⚡ Powerful Ninja!' },
      monitor: { bg: 'bg-orange-500/20', text: 'text-orange-300', border: 'border-orange-400', label: '🔥 Focus Energy!' },
      speedup: { bg: 'bg-red-500/20', text: 'text-red-300', border: 'border-red-400', label: '🥷 Unleash Power!' }
    };
    
    const config = statusConfig[status];
    
    return (
      <div className="flex items-center justify-between mb-6 relative z-10">
        <div className="flex items-center gap-3">
          <div className="text-2xl animate-pulse relative">
            🥷
            <div className="absolute -top-1 -right-1 text-xs animate-bounce">✨</div>
          </div>
          <div>
            <h3 className="text-xl font-bold text-white">Spinjitzu Energy Timeline</h3>
            <p className="text-orange-300 text-sm">Path to the Golden Nintendo Switch Mastery</p>
          </div>
        </div>
        <div className={`px-3 py-2 rounded-full text-sm font-bold border-2 ${config.bg} ${config.text} ${config.border} shadow-lg relative overflow-hidden`}>
          {config.label}
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent animate-pulse"></div>
        </div>
        
        {/* Floating energy particles background */}
        <div className="absolute top-0 left-0 w-full h-full pointer-events-none">
          <div className="absolute top-2 left-10 text-orange-400 opacity-60 animate-bounce animation-delay-100">✨</div>
          <div className="absolute top-8 right-20 text-yellow-400 opacity-40 animate-pulse animation-delay-200">🔥</div>
          <div className="absolute bottom-4 left-1/3 text-red-400 opacity-50 animate-bounce animation-delay-300">⚡</div>
          <div className="absolute top-4 right-10 text-blue-400 opacity-30 animate-pulse animation-delay-400">🌟</div>
        </div>
      </div>
    );
  }
  
  renderPredictionSection(): ReactNode {
    const projectedDate = this.calculateProjectedDate();
    const remaining = this.calculateRemaining();
    const vocabulary = this.getThemeVocabulary();
    
    return (
      <div className="bg-gradient-to-r from-orange-600/30 to-red-600/30 rounded-xl p-5 mb-6 border-2 border-orange-400 relative overflow-hidden">
        <div className="absolute top-0 right-0 text-6xl opacity-20">🏆</div>
        <div className="absolute -top-2 -left-2 w-16 h-16 bg-orange-500/20 rounded-full blur-xl animate-pulse"></div>
        <div className="absolute -bottom-2 -right-2 w-12 h-12 bg-yellow-500/20 rounded-full blur-lg animate-pulse animation-delay-500"></div>
        
        <div className="flex items-center gap-2 mb-3 relative z-10">
          <Calendar className="w-5 h-5 text-orange-300" />
          <span className="text-orange-300 font-semibold">
            Accumulating {this.props.dailyAverage.toFixed(1)} {vocabulary.unit} per training day...
          </span>
        </div>
        <div className="text-2xl font-bold text-white mb-2 relative z-10">
          ⚡ Master the {vocabulary.goal} in {this.formatTimeRemaining(projectedDate)}! 🏆
        </div>
        <div className="text-orange-200 relative z-10">
          Only {remaining} more {vocabulary.unit} needed! Your spinjitzu training is building legendary power! 🥷⚡
        </div>
        
        {/* Energy swirl effect */}
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-32 h-32 border-2 border-yellow-400/20 rounded-full animate-spin" style={{ animationDuration: '8s' }}></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-24 h-24 border border-orange-400/30 rounded-full animate-spin" style={{ animationDuration: '6s', animationDirection: 'reverse' }}></div>
      </div>
    );
  }
  
  renderProgressVisualization(): ReactNode {
    const progressPercentage = this.calculateProgressPercentage();
    const remaining = this.calculateRemaining();
    const vocabulary = this.getThemeVocabulary();
    
    // Ninja energy visualization instead of boring charts
    const generateEnergyVisualization = () => {
      const maxEnergy = this.props.targetPoints;
      const currentEnergy = this.props.currentPoints;
      const dailyEnergy = this.props.dailyAverage;
      const required = this.calculateRequiredDailyPace();
      
      // Energy levels based on daily performance
      const energyLevels = [
        { name: "Current Fire Power", value: dailyEnergy, maxValue: 50, color: "from-red-600 to-orange-600", icon: "🔥" },
        { name: "Required Lightning", value: required, maxValue: 50, color: "from-blue-600 to-purple-600", icon: "⚡" },
        { name: "Energy Progress", value: currentEnergy, maxValue: maxEnergy, color: "from-yellow-600 to-red-600", icon: "🌟" }
      ];
      
      return energyLevels;
    };
    
    // Ninja training intensity visualization
    const getTrainingIntensity = () => {
      const days = 14; // Show 14 days of training intensity
      const intensityData = [];
      
      for (let i = 0; i < days; i++) {
        const dayEnergy = Math.max(0, this.props.dailyAverage + (Math.random() - 0.5) * 20); // Simulate daily variation
        let intensity, color, height, icon;
        
        if (dayEnergy === 0) {
          intensity = "Rest Day";
          color = "bg-gray-600";
          height = 20;
          icon = "🏯";
        } else if (dayEnergy < 15) {
          intensity = "Light Training";
          color = "bg-orange-600";
          height = 30;
          icon = "🔥";
        } else if (dayEnergy < 30) {
          intensity = "Intense Training";
          color = "bg-red-600";
          height = 50;
          icon = "⚡";
        } else {
          intensity = "Master Level";
          color = "bg-yellow-500";
          height = 70;
          icon = "🌟";
        }
        
        intensityData.push({
          day: i + 1,
          energy: dayEnergy,
          intensity,
          color,
          height,
          icon
        });
      }
      
      return intensityData;
    };
    
    const energyLevels = generateEnergyVisualization();
    const trainingIntensity = getTrainingIntensity();
    
    return (
      <div className="space-y-6">
        {/* Ninja Energy Accumulation */}
        <div className="bg-black/40 rounded-xl p-5 border-2 border-orange-500 mb-4">
          <div className="flex items-center gap-2 mb-4">
            <Zap className="w-5 h-5 text-yellow-400" />
            <h4 className="font-bold text-white">Elemental Energy Accumulation</h4>
          </div>
          
          <div className="space-y-4">
            {energyLevels.map((level, index) => (
              <div key={index} className="relative">
                <div className="flex justify-between items-center mb-2">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">{level.icon}</span>
                    <span className="text-white font-semibold">{level.name}</span>
                  </div>
                  <span className="text-orange-300 font-bold">
                    {level.value.toFixed(1)} {index === 2 ? vocabulary.unit : `${vocabulary.unit}/day`}
                  </span>
                </div>
                
                <div className="relative h-6 bg-gray-800 rounded-full overflow-hidden border border-gray-600">
                  <div 
                    className={`h-full bg-gradient-to-r ${level.color} transition-all duration-1000 ease-out relative`}
                    style={{ width: `${Math.min((level.value / level.maxValue) * 100, 100)}%` }}
                  >
                    {/* Energy particles effect */}
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-pulse"></div>
                  </div>
                  
                  {/* Energy overflow effect for high values */}
                  {level.value > level.maxValue && (
                    <div className="absolute right-0 top-0 h-full w-2 bg-yellow-400 animate-pulse"></div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
        
        
      </div>
    );
  }
  
  renderMetrics(): ReactNode {
    const requiredPace = this.calculateRequiredDailyPace();
    const vocabulary = this.getThemeVocabulary();
    const statusMessage = this.getStatusMessage();
    const status = this.determineStatus();

    return (
      <div className="bg-gradient-to-br from-gray-800 to-black rounded-lg p-4 mb-6 border-2 border-orange-500/50 relative overflow-hidden shadow-lg">
        <div className="absolute top-2 right-2 text-2xl opacity-50">🎯</div>
        <h4 className="font-bold text-lg text-orange-300 text-center">Required Energy</h4>
        <div className="text-center my-2">
          <div className="text-4xl font-bold text-white">{requiredPace.toFixed(1)}</div>
          <div className="text-sm font-semibold text-white/80">{vocabulary.pace}</div>
        </div>

        {(status === 'monitor' || status === 'speedup') && statusMessage.suggestions.length > 0 && (
          <div className="border-t border-orange-500/30 mt-4 pt-3">
            <div className="text-xs text-orange-200 mb-2 text-center">🥷 Ninja training boosts:</div>
            <div className="flex flex-wrap gap-2 justify-center">
              {statusMessage.suggestions.map((suggestion, i) => (
                <span key={i} className="text-xs bg-orange-600/30 text-orange-200 px-2 py-1 rounded-full border border-orange-500/40">
                  {suggestion.replace('Sanskrit', 'Ancient Wisdom').replace('Math', 'Combat Skills').replace('Reading', 'Scrolls')}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }
}

// React component that creates and uses the Ninjago implementation
export const NinjagoTrajectoryCard: React.FC<BaseTrajectoryProps> = (props) => {
  const implementation = new NinjagoTrajectoryCardImplementation(props);
  return <TrajectoryCardRenderer {...props} implementation={implementation} />;
};
